import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Card, Select, SelectItem, Tab, TabGroup, TabList, Text, Title } from '@tremor/react';
import Header from '../components/Header';
import DateRangePresets from '../components/DateRangePresets';
import AccessDenied from '../components/AccessDenied';
import EmptyState from '../components/EmptyState';
import ErrorRetry from '../components/ErrorRetry';
import TableSkeleton from '../components/TableSkeleton';
import { currentUser } from '../api/auth';
import { fetchServiceOrders } from '../api/service';
import { useStoreFilter } from '../lib/storeFilter';
import { localYMD } from '../lib/format';
import { t } from '../lib/i18n';
import type { ServiceOrder, User } from '../api/types';
import Resumen from './service/Resumen';
import Tablero from './service/Tablero';
import Agenda from './service/Agenda';
import Orden360 from './service/Orden360';
import Vehiculos from './service/Vehiculos';
import Tecnicos from './service/Tecnicos';
import Rendimiento from './service/Rendimiento';
import Dvi from './service/Dvi';
import PrepararFacturacion from './service/PrepararFacturacion';

type View = 'resumen' | 'tablero' | 'agenda' | 'orden' | 'vehiculos' | 'tecnicos' | 'rendimiento' | 'facturar' | 'dvi';
const VIEWS: View[] = ['resumen', 'tablero', 'agenda', 'orden', 'vehiculos', 'tecnicos', 'rendimiento', 'facturar'];
type StatusFilter = 'all' | 'open' | 'complete';

function dateNDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localYMD(d);
}

export default function Service() {
  // Tri-state currentUser load: undefined = pending; null = redirect; User = render.
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  return user ? <Cockpit user={user} /> : <Navigate to="/login" replace />;
}

/**
 * Service Cockpit shell. Owns the view switcher, the shared filters
 * (date range, store, status, tech) and the single `/api/service-orders`
 * fetch that every list-oriented view reads. Mirrors the multi-view Caja
 * idiom: a Tremor TabGroup drives `view`, panels render below it.
 */
function Cockpit({ user }: { user: User }) {
  const enabled = !!user.features?.service_shop;
  const authorized = user.role === 'owner' || user.role === 'manager';
  // DVI ("Inspección") is a feature-gated add-on tab — only appended to the
  // tab list when the customer has dvi_enabled, so an off-module never shows
  // a dead tab. See VIEWS (module-level, ungated) vs. views (gated, used by
  // the TabGroup/TabList below).
  const views: View[] = user.features?.dvi_enabled ? [...VIEWS, 'dvi'] : VIEWS;

  const [view, setView] = useState<View>('resumen');
  const [from, setFrom] = useState(dateNDaysAgo(30));
  const [to, setTo] = useState(dateNDaysAgo(0));
  // Shared header store (lib/storeFilter) — persists across pages.
  const [sid] = useStoreFilter(user);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [tech, setTech] = useState<string>(''); // '' = all techs (client-side filter)
  const [rows, setRows] = useState<ServiceOrder[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Date / store / status hit the server; tech is filtered client-side so the
  // tech dropdown can list exactly the technicians present in the result set.
  const load = () => {
    if (!enabled || !authorized) return;
    setErr(null);
    setRows(null);
    fetchServiceOrders({ from, to, sid, status })
      .then(setRows)
      .catch((e: Error) => setErr(e.message ?? String(e)));
  };
  useEffect(() => { load(); }, [enabled, authorized, from, to, sid, status]);

  const techOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of rows ?? []) if (r.tech) m.set(r.tech, r.tech_name || r.tech);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  // The shared, tech-scoped order set every view reads from.
  const orders = useMemo(() => {
    const all = rows ?? [];
    return tech ? all.filter((r) => r.tech === tech) : all;
  }, [rows, tech]);

  // ── View wiring ────────────────────────────────────────────────────────────
  // All five views are live. openWo records the WO selection and switches to the
  // Orden 360 tab, which reads `selWo`; openVeh does the same for the Vehículos
  // tab, which reads `selVeh`. Técnicos needs no selection state — it fetches its
  // own commission set from the shared from/to/sid window.
  const [selWo, setSelWo] = useState<{ sid: number; tnum: number } | null>(null);
  const openWo = (s: number, tn: number) => { setSelWo({ sid: s, tnum: tn }); setView('orden'); };
  const [selVeh, setSelVeh] = useState<number | null>(null);
  const openVeh = (equip: number) => { setSelVeh(equip); setView('vehiculos'); };

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} storeFilter={enabled && authorized} />
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        <div>
          <Title>{t.service.title}</Title>
          <Text className="text-tremor-content text-sm">{t.service.subtitle}</Text>
        </div>

        {!enabled ? (
          <EmptyState message={t.service.disabled} />
        ) : !authorized ? (
          <AccessDenied user={user} />
        ) : (
          <>
            <Card className="flex flex-col md:flex-row md:items-center gap-3">
              <DateRangePresets from={from} to={to} onChange={({ from: f, to: t2 }) => { setFrom(f); setTo(t2); }} />
              <div className="flex gap-3 md:ml-auto">
                <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)} className="w-40" enableClear={false}>
                  <SelectItem value="all">{t.service.statusAll}</SelectItem>
                  <SelectItem value="open">{t.service.statusOpen}</SelectItem>
                  <SelectItem value="complete">{t.service.statusComplete}</SelectItem>
                </Select>
                <Select value={tech} onValueChange={setTech} className="w-48" enableClear={false}>
                  <SelectItem value="">{t.service.filters.allTechs}</SelectItem>
                  {techOptions.map(([code, name]) => (
                    <SelectItem key={code} value={code}>{name}</SelectItem>
                  ))}
                </Select>
              </div>
            </Card>

            <TabGroup index={views.indexOf(view)} onIndexChange={(i) => setView(views[i])}>
              <TabList>
                {views.map((v) => (
                  <Tab key={v}>{t.service.cockpit.views[v]}</Tab>
                ))}
              </TabList>
            </TabGroup>

            {err && <ErrorRetry message={err} onRetry={load} />}
            {!err && rows === null && <TableSkeleton rows={6} cols={6} />}
            {!err && rows !== null && (
              view === 'resumen' ? (
                <Resumen orders={orders} />
              ) : view === 'tablero' ? (
                <Tablero orders={orders} onOpen={openWo} />
              ) : view === 'agenda' ? (
                <Agenda user={user} sid={sid} onOpenWo={openWo} />
              ) : view === 'orden' ? (
                <Orden360 sel={selWo} onPick={openWo} orders={orders} />
              ) : view === 'facturar' ? (
                <PrepararFacturacion user={user} sel={selWo} orders={orders} onPick={openWo} />
              ) : view === 'vehiculos' ? (
                <Vehiculos orders={orders} sel={selVeh} onSelect={openVeh} />
              ) : view === 'dvi' ? (
                <Dvi orders={orders} sid={sid} />
              ) : view === 'rendimiento' ? (
                <Rendimiento sid={sid} />
              ) : (
                <Tecnicos from={from} to={to} sid={sid} />
              )
            )}
          </>
        )}
      </main>
    </div>
  );
}
